<script setup lang="ts">
// Vendor
import { useDebounceFn } from "@vueuse/core";

const { autoResize, debounce } = defineProps<{
  /**
   * Automatically resize the textarea to fit its content when set to "true"
   */
  autoResize?: boolean;
  /**
   * Debounce time in milliseconds. Set to false to disable. Defaults to "undefined"
   */
  debounce?: string | number | false;
}>();

const field = useFormControl();

if (!field)
  throw new Error(
    "[FormTextarea]: field instance not available. Did you wrap this component inside a FormControl?",
  );

const fieldRef = ref<HTMLTextAreaElement | null>(null);

const _name = unref(field.name);

const _handleModelChange = useDebounceFn(
  (value?: string) => {
    field.setValue(value || undefined, field.validateOnValueUpdate);
  },
  typeof debounce === "string" ? parseInt(`${debounce || 0}`) : debounce || 0,
);

function resizeTextarea(value?: string) {
  if (!fieldRef.value) {
    return;
  }

  /**
   * First remove the style to correctly re-calculate the height
   */
  fieldRef.value.removeAttribute("style");

  const textAreaRect = fieldRef.value.getBoundingClientRect();

  if (value) {
    fieldRef.value.style.minHeight = `${Math.max(
      fieldRef.value.scrollHeight,
      textAreaRect.height,
    )}px`;
  }
}

const inputModel = defineModel<string | undefined>({
  /**
   * Trigger possibly debounced update of vee-validate form value when v-model updates
   */
  set(value) {
    _handleModelChange(value);

    if (autoResize) {
      resizeTextarea(value);
    }

    return value;
  },
});

if (field.value.value) {
  inputModel.value =
    typeof field.value.value === "string"
      ? field.value.value
      : String(field.value.value || "");
}

defineExpose({
  field,
  fieldRef,
  inputModel,
});
</script>

<template>
  <textarea
    v-model="inputModel"
    :id="field.inputId || _name"
    class="form-textarea"
    :name="_name"
    ref="fieldRef"
  />
</template>

<style scoped>
.form-textarea {
  order: 1;
  box-sizing: border-box;
  width: 100%;
  height: var(--form-textarea-initial-height, 90px);
  min-height: var(--form-textarea-min-height, 90px);
  font-size: var(--form-input-font-size, 1rem);
  font-family: inherit;
  line-height: var(--input-line-height, 1.5);
  border: var(--form-input-border-width, 1px) solid
    var(--form-input-border-color, #ddd);
  border-radius: var(--form-input-border-radius, 4px);
  resize: vertical;
  padding-inline: var(--form-input-padding-inline, 16px);
  padding-block: var(--form-input-padding-block-start, 26px)
    var(--form-input-padding-block-end, 8px);
}
</style>
